"use client"

import { useEffect, useMemo, useState, type JSX, type ReactNode } from "react"
import Link from "next/link"
import { usePostHog } from "posthog-js/react"
import {
  ArrowUpRight,
  Calculator,
  Calendar,
  ChefHat,
  Clock,
  DollarSign,
  HelpCircle,
  Home,
  Package,
  Ruler,
  Search,
  ShieldCheck,
  Thermometer,
  Warehouse,
  Wrench,
  Zap,
} from "lucide-react"
import { SITE_STATS } from "@/lib/site-stats"
import { TOOLS_INDEX_FAQS } from "@/lib/tools/tools-index-faqs"
import { fetchWithDelay } from "@/lib/api-fetch"
import {
  Breadcrumb,
  CategoryFilterPill,
  FAQAccordion,
  FeaturedAccentCard,
  IndexSectionHeader,
  NumberedStepList,
  ShowMoreButton,
  StatBorderGrid,
} from "@/components/brand-kit"
import { EDITORIAL_HERO_BREADCRUMB_CLASSES } from "@/components/brand-kit/editorial-hero"
import { toolsBreadcrumbs } from "@/lib/breadcrumbs"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

// ---------- Data sources tooltip ----------
// Summarizes the authoritative sources the calculators rely on. Mirrors the
// fuller description in the sitewide footer so people can trust the numbers
// without scrolling to the bottom of the page.
const DATA_SOURCES: { label: string; detail: string }[] = [
  { label: "AHAM", detail: "Capacity & performance standards." },
  { label: "DOE", detail: "Certified product database." },
  { label: "ENERGY STAR", detail: "Efficient-model certifications." },
  { label: "EIA", detail: "Local electricity rates." },
  { label: "ASHRAE", detail: "Thermal engineering standards." },
  { label: "U.S. Census", detail: "ZIP & city demographic data." },
]

function DataSourcesInfo({
  triggerClassName = "",
  label = "About the data sources",
}: {
  triggerClassName?: string
  label?: string
}) {
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-label={label}
        className={`inline-flex items-center justify-center text-gray-500 hover:text-[#0469f0] transition-colors align-middle focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/30 rounded-full ${triggerClassName}`}
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </PopoverTrigger>
      <PopoverContent
        align="center"
        side="bottom"
        sideOffset={6}
        collisionPadding={16}
        className="w-[260px] p-3 bg-white text-gray-900 border-gray-200 rounded-md"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#ff9951] mb-1.5">Data sources</p>
        <p className="text-[11px] text-gray-600 leading-snug mb-2">
          Source references vary by calculator. Check the individual tool for its inputs and assumptions.
        </p>
        <ul className="space-y-1 text-[11px] leading-snug">
          {DATA_SOURCES.map((s) => (
            <li key={s.label}>
              <span className="font-semibold text-gray-900">{s.label}</span>
              <span className="text-gray-600"> — {s.detail}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

// ---------- Types ----------
interface Tool {
  name: string
  slug: string
  description: string
  icon: JSX.Element
  category: string
  /** Normalized UI bucket — populated after tools are loaded, used by filter + card label. */
  bucket?: string
  popular: boolean
}

interface ToolsClientProps {
  initialData?: {
    tools: Tool[]
  } | null
}

// The raw `tools.category` column has 15 values with duplicates and
// overlapping buckets (e.g. "Energy & Cost"/"Energy & Savings",
// "Size & Capacity"/"Sizing & Capacity"). Normalize to 6 clean buckets
// for the filter UI — SEO content in page.tsx keeps the raw categories.
function normalizeCategory(raw: string): string {
  const c = raw.trim().toLowerCase()
  if (c.includes("energy") || c.includes("cost") || c.includes("value")) return "Energy & Cost"
  if (c.includes("size") || c.includes("capacity")) return "Size & Capacity"
  if (c.includes("food") || c.includes("storage") || c.includes("safety")) return "Food Storage"
  if (c.includes("maintenance") || c.includes("ownership") || c.includes("technical")) return "Maintenance"
  if (c.includes("recipe") || c.includes("cooking")) return "Cooking"
  if (c.includes("specialty") || c.includes("beverage") || c.includes("commercial") || c.includes("freezer organization")) return "Specialty"
  return "Specialty"
}

// Icon mapping — covers every icon_name currently in the tools table,
// with Calculator as the fallback.
const iconMap: Record<string, JSX.Element> = {
  Calculator: <Calculator className="w-6 h-6" />,
  Calendar: <Calendar className="w-6 h-6" />,
  Clock: <Clock className="w-6 h-6" />,
  DollarSign: <DollarSign className="w-6 h-6" />,
  Home: <Home className="w-6 h-6" />,
  Package: <Package className="w-6 h-6" />,
  Ruler: <Ruler className="w-6 h-6" />,
  Search: <Search className="w-6 h-6" />,
  ShieldCheck: <ShieldCheck className="w-6 h-6" />,
  Thermometer: <Thermometer className="w-6 h-6" />,
  Warehouse: <Warehouse className="w-6 h-6" />,
  Wrench: <Wrench className="w-6 h-6" />,
  Zap: <Zap className="w-6 h-6" />,
  ChefHat: <ChefHat className="w-6 h-6" />,
}

// ---------- Small pieces ----------

function ToolEyebrow({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`text-xs font-bold uppercase tracking-[0.15em] text-[#ff9951] ${className}`}
    >
      {children}
    </div>
  )
}

// ---------- Hero ----------

function ToolsHero({ toolCount, categoryCount }: { toolCount: number; categoryCount: number }) {
  const stats = [
    {
      v: String(toolCount),
      l: "Calculators & tools",
      note: `Across ${categoryCount} categories`,
    },
    { v: SITE_STATS.states, l: "States covered", note: "Real electricity rates" },
    {
      v: "AHAM",
      l: "Data source",
      note: (
        <span className="inline-flex items-center gap-1.5">
          <span>DOE + EIA references</span>
          <DataSourcesInfo />
        </span>
      ),
    },
    { v: "$0", l: "Signup cost", note: "Forever" },
  ]

  return (
    <section className="bg-white border-b border-gray-200">
      <div className="max-w-[1320px] mx-auto px-6 md:px-8 pt-10 sm:pt-14 md:pt-[72px] pb-10 md:pb-12">
        {/* Breadcrumb — ONE trail (lib/breadcrumbs.ts): ToolsIndexSchemas emits
            the same toolsBreadcrumbs() as the BreadcrumbList; the schema had no
            visible counterpart before FRIDGE-269. Same look as every tool hero. */}
        <Breadcrumb
          trail={toolsBreadcrumbs()}
          className={EDITORIAL_HERO_BREADCRUMB_CLASSES.nav}
          listClassName={EDITORIAL_HERO_BREADCRUMB_CLASSES.list}
          itemClassName={EDITORIAL_HERO_BREADCRUMB_CLASSES.item}
          linkClassName={EDITORIAL_HERO_BREADCRUMB_CLASSES.link}
          currentClassName={EDITORIAL_HERO_BREADCRUMB_CLASSES.current}
          separatorClassName={EDITORIAL_HERO_BREADCRUMB_CLASSES.separator}
        />
        <div className="tools-hero-grid grid gap-8 md:gap-10 lg:gap-16 items-end">
          {/* Left — editorial headline */}
          <div>
            <ToolEyebrow>◉ &nbsp; Free tools &nbsp;·&nbsp; Trusted by {SITE_STATS.users} shoppers</ToolEyebrow>
            {/* Keyword-placement standard (plan item 15): H1 + first sentence
                carry the head term ("refrigerator … calculators"), matching
                the page title and the sr-only H1 in page.tsx. */}
            <h1
              className="mt-5 font-black tracking-[-0.035em] leading-[0.95] text-gray-900"
              style={{ fontSize: "clamp(34px, 5vw, 72px)" }}
            >
              Refrigerator &amp; freezer tools to shop <span className="italic font-black text-[#ff9951]">smarter</span>.
            </h1>
            <p className="mt-5 text-gray-600 leading-[1.55] max-w-xl text-base sm:text-lg">
              Compare size, running costs, food storage and maintenance needs.
              Free calculators and guides, with no signup or email wall.
            </p>
          </div>

          {/* Right — 2x2 stat grid with black borders (brand-kit) */}
          <StatBorderGrid
            stats={stats.map((s) => ({ value: s.v, label: s.l, note: s.note }))}
            className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
          />
        </div>
      </div>
    </section>
  )
}

// ---------- Filter strip ----------

// Mobile shows this many pills before the "+N more" expander to keep the sticky
// strip compact. Desktop always shows all pills.
const MOBILE_PILL_CAP = 3

function ToolsFilter({
  categories,
  counts,
  filter,
  setFilter,
}: {
  categories: string[]
  counts: Record<string, number>
  filter: string
  setFilter: (v: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)")
    const sync = () => setIsMobile(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  const allPills = [
    { label: "All tools", key: "all" },
    ...categories.map((c) => ({ label: c, key: c })),
  ]
  // Always surface the currently active pill on mobile so users can see what's
  // selected without expanding.
  const activeIndex = allPills.findIndex((p) => p.key === filter)
  const baseMobileIndices = new Set<number>()
  for (let i = 0; i < Math.min(MOBILE_PILL_CAP, allPills.length); i++) baseMobileIndices.add(i)
  if (activeIndex >= 0) baseMobileIndices.add(activeIndex)
  const remaining = allPills.length - baseMobileIndices.size
  const showCollapsed = isMobile && !expanded

  return (
    <section className="bg-gray-50 border-b border-gray-200 sticky top-[78px] sm:top-[92px] z-40 backdrop-blur-sm supports-[backdrop-filter]:bg-gray-50/95">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 md:px-8 py-2.5 sm:py-5 flex items-center gap-2 sm:gap-4 flex-wrap">
        <div className="hidden sm:block text-[10px] font-bold uppercase tracking-[0.15em] text-gray-900 mr-1">
          Browse by
        </div>
        <div className="flex gap-1.5 sm:gap-2 flex-wrap items-center">
          {allPills.map((p, i) => {
            const isBase = baseMobileIndices.has(i)
            const hide = showCollapsed && !isBase
            return (
              <span key={p.key} style={hide ? { display: "none" } : undefined}>
                <CategoryFilterPill
                  label={p.label}
                  count={counts[p.key] ?? 0}
                  active={filter === p.key}
                  onClick={() => setFilter(p.key)}
                  className=""
                />
              </span>
            )
          })}
          {isMobile && remaining > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-gray-200 bg-white px-3 sm:px-4 py-1 sm:py-2 text-[12px] sm:text-[13px] font-semibold tracking-[0.01em] text-gray-700 hover:border-[#ff9951] hover:text-[#ff9951] transition-colors shadow-sm"
              aria-expanded={expanded}
            >
              {expanded ? "Show less" : `+${remaining} more`}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

// ---------- Standard tool card ----------

function ToolCard({ tool, num }: { tool: Tool; num: number }) {
  return (
    <Link
      href={`/tools/${tool.slug}`}
      className="group relative flex flex-col gap-2 sm:gap-3 rounded-md border border-gray-200 bg-white p-3 sm:p-5 min-h-[150px] sm:min-h-[200px] transition-all duration-[180ms] hover:-translate-y-[3px] hover:border-[#ff9951] hover:shadow-[0_20px_25px_-5px_rgba(255,153,81,0.15),0_8px_10px_-6px_rgba(255,153,81,0.08)]"
    >
      {/* Top row: icon + number */}
      <div className="flex justify-between items-start">
        <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg bg-[#0469f0]/10 text-[#0469f0] flex items-center justify-center flex-shrink-0 [&_svg]:w-5 [&_svg]:h-5 sm:[&_svg]:w-6 sm:[&_svg]:h-6">
          {tool.icon}
        </div>
        <span className="font-mono text-[11px] sm:text-[12px] font-semibold tracking-[0.02em] text-gray-500">
          № {String(num).padStart(2, "0")}
        </span>
      </div>

      {/* Title + badges */}
      <div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap mb-1.5 sm:mb-2">
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] sm:tracking-[0.15em] text-[#0469f0]">
            {tool.bucket ?? tool.category}
          </span>
          {tool.popular && (
            <span className="bg-[#ff9951] text-[var(--color-brand-action-text)] text-[9px] sm:text-[10px] font-bold uppercase tracking-widest px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full">
              Popular
            </span>
          )}
        </div>
        <h3 className="text-[13px] sm:text-[20px] font-extrabold tracking-[-0.02em] text-gray-900 leading-[1.2]">
          {tool.name}
        </h3>
        <p className="mt-1 sm:mt-2 text-[11px] sm:text-sm text-gray-600 leading-[1.4] sm:leading-[1.55]">
          {tool.description}
        </p>
      </div>

      {/* Footer — "Open →" CTA, hidden on mobile since the whole card is clearly tappable */}
      <div className="mt-auto hidden sm:flex justify-end pt-2">
        <span className="text-[#ff9951] font-bold text-[13px] inline-flex items-center gap-1 group-hover:gap-1.5 transition-[gap]">
          Open <ArrowUpRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  )
}

// ---------- Grid ----------

function ToolsGrid({
  tools,
  featured,
  showFeatured,
  totalCount,
  canLoadMore,
  onLoadMore,
}: {
  tools: Tool[]
  featured: Tool | null
  showFeatured: boolean
  totalCount: number
  canLoadMore: boolean
  onLoadMore: () => void
}) {
  return (
    <section className="bg-white py-10 sm:py-14">
      <div className="max-w-[1320px] mx-auto px-6 md:px-8">
        <IndexSectionHeader
          title="The index"
          meta={`${totalCount} ${totalCount === 1 ? "tool" : "tools"} · browse by category`}
        />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {showFeatured && featured && (
            <FeaturedAccentCard
              href={`/tools/${featured.slug}`}
              icon={featured.icon}
              eyebrow={featured.bucket ?? featured.category}
              title={featured.name}
              description={featured.description}
              badge="Featured"
              ctaLabel="Start now"
              className="col-span-2 lg:col-span-2"
            />
          )}
          {tools.map((t, i) => (
            <ToolCard key={t.slug} tool={t} num={showFeatured && featured ? i + 2 : i + 1} />
          ))}
        </div>

        {tools.length === 0 && !showFeatured && (
          <div className="py-20 text-center text-gray-500">No tools in this category.</div>
        )}

        {canLoadMore && <ShowMoreButton onClick={onLoadMore} className="mt-10" />}
      </div>
    </section>
  )
}

// ---------- How it works ----------

function HowItWorks() {
  const steps = [
    {
      number: "01",
      title: "Enter your details",
      description: "Enter the inputs your chosen tool asks for, such as size, age or electricity rate.",
    },
    {
      number: "02",
      title: "Review the result",
      description: (
        <span className="inline-flex items-baseline flex-wrap gap-x-1.5">
          <span>See the estimate or guidance, and check the inputs and assumptions used.</span>
          <DataSourcesInfo />
        </span>
      ),
    },
    {
      number: "03",
      title: "Choose your next step",
      description: "Use the result to compare models, plan an installation or maintain your fridge or freezer.",
    },
  ]

  return (
    <section className="bg-gray-50 border-t border-gray-200 py-12 sm:py-16 md:py-20">
      <div className="max-w-[1320px] mx-auto px-6 md:px-8">
        <div className="tools-howitworks-grid grid gap-10 md:gap-12 lg:gap-20 items-start">
          <div>
            <ToolEyebrow>How it works</ToolEyebrow>
            <h2
              className="mt-4 font-black tracking-[-0.03em] leading-none text-gray-900"
              style={{ fontSize: "clamp(30px, 3.5vw, 48px)" }}
            >
              Three steps,
              <br />
              <em className="italic text-[#ff9951] font-black">zero fluff.</em>
            </h2>
            <p className="mt-5 text-[14px] sm:text-[15px] text-gray-600 leading-[1.6] max-w-[400px]">
              We don&apos;t sell appliances. Our calculators exist so you can make one
              honest comparison and move on with your day.
            </p>
          </div>

          <NumberedStepList steps={steps} />
        </div>
      </div>
    </section>
  )
}

// ---------- Questions ----------

// FRIDGE-1440: the three questions the /tools FAQPage node carries, shown on the
// page from the same list (lib/tools/tools-index-faqs.ts). Every answer is in the
// server HTML; closed ones are hidden, not absent (FRIDGE-1441).
function ToolsFaq() {
  return (
    <section className="bg-white border-t border-gray-200 py-12 sm:py-16 md:py-20">
      <div className="max-w-[1320px] mx-auto px-6 md:px-8">
        <div className="max-w-[880px]">
          <ToolEyebrow>Questions</ToolEyebrow>
          <h2
            className="mt-4 font-black tracking-[-0.03em] leading-none text-gray-900"
            style={{ fontSize: "clamp(30px, 3.5vw, 48px)" }}
          >
            Frequently asked
          </h2>
          <FAQAccordion
            items={[...TOOLS_INDEX_FAQS]}
            columns={1}
            defaultOpen={[0]}
            title=""
            bordered={false}
          />
        </div>
      </div>
    </section>
  )
}

// ---------- Page ----------

export default function ToolsClient({ initialData }: ToolsClientProps) {
  const posthog = usePostHog()
  const [filter, setFilter] = useState<string>("all")
  // Pagination in grid slots (not cards) so the trailing row is always even —
  // the featured card occupies 2 slots in the 4-col desktop grid. 16 slots =
  // 4 rows, which renders as (1 featured + 14 regular) = 15 cards when the
  // featured is present, or 16 regular cards when it isn't.
  const [visibleSlots, setVisibleSlots] = useState<number>(16)
  const [tools, setTools] = useState<Tool[]>(initialData?.tools || [])
  const [loading, setLoading] = useState(!initialData)
  const [error, setError] = useState<string | null>(null)

  // Reset visible slot count whenever the filter changes so a fresh pill
  // click shows all matching tools without leftover pagination state.
  //
  // ADJUSTED DURING RENDER, not from an effect — React's documented pattern for
  // state derived from another value, and what react-hooks/set-state-in-effect
  // asks for. The effect version committed one frame with the previous filter's
  // slot count before correcting it.
  const [prevFilter, setPrevFilter] = useState(filter)
  if (prevFilter !== filter) {
    setPrevFilter(filter)
    setVisibleSlots(16)
  }

  // Fallback fetch for direct navigation without SSR data
  useEffect(() => {
    if (initialData) return
    let cancelled = false
    async function fetchTools() {
      try {
        const response = await fetchWithDelay("/api/tools")
        if (!response.ok) throw new Error(`API returned ${response.status}`)
        const { tools: data } = await response.json()
        if (cancelled) return
        if (data && data.length > 0) {
          const formatted: Tool[] = data.map((t: any) => ({
            name: t.name,
            slug: t.slug,
            description: t.description || "",
            icon: iconMap[t.icon_name] || <Calculator className="w-6 h-6" />,
            category: t.category || "Other",
            popular: t.popular || false,
          }))
          setTools(formatted)
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Failed to load tools")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchTools()
    return () => {
      cancelled = true
    }
  }, [initialData])

  // Track tools page view
  useEffect(() => {
    if (posthog && tools.length > 0) {
      posthog.capture("tools_page_viewed", {
        tool_count: tools.length,
        categories: [...new Set(tools.map((t) => t.category))],
      })
    }
  }, [posthog, tools.length])

  // Tools tagged with a normalized bucket category for the filter UI.
  const normalizedTools = useMemo(
    () => tools.map((t) => ({ ...t, bucket: normalizeCategory(t.category) })),
    [tools],
  )

  // Deterministic bucket order (matches handoff: energy → sizing → food → maintenance → cooking → specialty).
  const categoryOrder = [
    "Energy & Cost",
    "Size & Capacity",
    "Food Storage",
    "Maintenance",
    "Cooking",
    "Specialty",
  ]
  const categories = useMemo(() => {
    const present = new Set(normalizedTools.map((t) => t.bucket))
    return categoryOrder.filter((c) => present.has(c))
  }, [normalizedTools])

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: normalizedTools.length }
    for (const t of normalizedTools) c[t.bucket] = (c[t.bucket] || 0) + 1
    return c
  }, [normalizedTools])

  // Featured = first popular tool (server already sorts by sort_order)
  const featured = useMemo(
    () => normalizedTools.find((t) => t.popular) || null,
    [normalizedTools],
  )

  const filtered = useMemo(
    () => (filter === "all" ? normalizedTools : normalizedTools.filter((t) => t.bucket === filter)),
    [normalizedTools, filter],
  )

  // Show featured card only when it belongs to the current filter
  const showFeatured = !!featured && (filter === "all" || featured.bucket === filter)
  const othersInGrid = showFeatured && featured
    ? filtered.filter((t) => t.slug !== featured.slug)
    : filtered

  // Pagination: only cap results when filter is "all". Any specific category
  // filter shows every matching tool (no "Show more" needed). Cap is in grid
  // slots — featured takes 2 slots in the 4-col desktop grid, regulars 1.
  const isAllFilter = filter === "all"
  const featuredSlots = showFeatured ? 2 : 0
  const othersLimit = isAllFilter
    ? Math.max(0, visibleSlots - featuredSlots)
    : othersInGrid.length
  const visibleOthers = othersInGrid.slice(0, othersLimit)
  const hiddenCount = othersInGrid.length - visibleOthers.length
  const canLoadMore = isAllFilter && hiddenCount > 0

  // Loading state
  if (loading) {
    return (
      <main id="main-content" className="max-w-[1320px] mx-auto px-6 py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0469f0] mx-auto mb-4" />
          <p className="text-gray-600">Loading tools…</p>
        </div>
      </main>
    )
  }

  if (error) {
    return (
      <main id="main-content" className="max-w-[1320px] mx-auto px-6 py-20">
        <div className="text-center text-red-600">
          <p className="text-xl font-bold mb-2">Error loading tools</p>
          <p>{error}</p>
        </div>
      </main>
    )
  }

  if (tools.length === 0) {
    return (
      <main id="main-content" className="max-w-[1320px] mx-auto px-6 py-20">
        <div className="text-center text-gray-600">No tools available.</div>
      </main>
    )
  }

  return (
    <main id="main-content">
      <style>{`
        .tools-hero-grid { grid-template-columns: 1fr; }
        .tools-howitworks-grid { grid-template-columns: 1fr; }
        @media (min-width: 768px) {
          .tools-hero-grid { grid-template-columns: 1.4fr 1fr; }
          .tools-howitworks-grid { grid-template-columns: 1fr 1.3fr; }
        }
      `}</style>
      <ToolsHero toolCount={tools.length} categoryCount={categories.length} />
      <ToolsFilter
        categories={categories}
        counts={counts}
        filter={filter}
        setFilter={setFilter}
      />
      <ToolsGrid
        tools={visibleOthers}
        featured={featured}
        showFeatured={showFeatured}
        totalCount={filtered.length}
        canLoadMore={canLoadMore}
        onLoadMore={() => setVisibleSlots((s) => s + 16)}
      />
      <HowItWorks />
      <ToolsFaq />
    </main>
  )
}
// bust 1776851100
